import React, { useState, useEffect, useMemo } from 'react'
import {
  Building2,
  MapPin,
  Users,
  DollarSign,
  TrendingUp,
  Clock,
  Car,
  Waves,
  Dumbbell,
  BarChart3,
  Target,
  Calendar,
  Percent,
  Ruler,
  Home,
  Loader2
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

interface ProjectStatisticsProps {
  projectId: number
}

// Compact StatCard component for better space utilization
const CompactStatCard = ({ 
  icon: Icon, 
  title, 
  value, 
  subtitle, 
  color = 'blue',
  isLoading = false 
}: {
  icon: any
  title: string
  value: string | number
  subtitle?: string
  color?: 'blue' | 'green' | 'purple' | 'red' | 'yellow' | 'gray'
  isLoading?: boolean
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    gray: 'bg-gray-50 text-gray-600'
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Icon className="h-4 w-4" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {title}
            </h3>
            <div className="mt-1">
              {isLoading ? (
                <div className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
              ) : (
                <p className="text-lg font-semibold text-gray-900 truncate">
                  {value}
                </p>
              )}
              {subtitle && !isLoading && (
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface DetailedProjectStats {
  // Basic project info
  projectId: number
  name: string
  address: string
  status: string
  listingDate: string
  timeSinceListingMonths: number
  
  // Entity relationships
  architect: string
  developer: string
  broker: string
  
  // Location info
  area: string
  neighbourhood: string
  city: string
  district: string
  
  // Unit statistics
  totalUnits: number
  availableUnits: number
  soldUnits: number
  reservedUnits: number
  conversionRate: number
  
  // Price metrics
  minPrice: number
  maxPrice: number
  avgPrice: number
  minSqmPrice: number
  maxSqmPrice: number
  avgSqmPrice: number
  
  // GPA metrics
  minGpa: number
  maxGpa: number
  avgGpa: number
  minTotalGpa: number
  maxTotalGpa: number
  avgTotalGpa: number
  
  // Parking metrics
  unitsWithParking: number
  avgParkingSpaces: number
  parkingPercentage: number
  
  // Time metrics
  avgDaysToSell: number
  avgMonthsToSell: number
  earliestListing: string
  latestListing: string
  
  // Yield metrics
  avgYieldPercentage: number
  minYieldPercentage: number
  maxYieldPercentage: number
  
  // Project amenities
  hasParking: boolean
  hasPool: boolean
  hasGym: boolean
  
  // Project yield
  projectYield: number
  yieldDuration: number
  
  // Scraping info
  lastScrapedAt: string
  scrapStatus: string
  scrapErrorMessage: string
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

const formatNumber = (num: number, decimals = 0) => {
  return new Intl.NumberFormat('pt-PT', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num)
}

const formatPercentage = (num: number | null | undefined, decimals = 1) =>
  num === null || num === undefined || Number.isNaN(num) ? '—' : `${formatNumber(num, decimals)}%`

const StatCard = ({ 
  icon: Icon, 
  title, 
  value, 
  subtitle, 
  color = 'blue',
  className = ''
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  value: string | number
  subtitle?: string
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'indigo'
  className?: string
}) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
    indigo: 'bg-indigo-100 text-indigo-600',
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="text-right flex-1">
          <p className="text-sm text-gray-500 uppercase tracking-wide font-medium">
            {title}
          </p>
          <div className="text-2xl font-bold text-gray-900">
            {value}
          </div>
          {subtitle && (
            <div className="text-xs text-gray-500 mt-1">
              {subtitle}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function ProjectStatistics({ projectId }: ProjectStatisticsProps) {
  const [loadedKPIs, setLoadedKPIs] = useState<Set<string>>(new Set())
  const [loadingKPIs, setLoadingKPIs] = useState<Set<string>>(new Set())
  const [selectedTypology, setSelectedTypology] = useState<string | null>(null)

  // Main statistics query with lazy loading
  const { data: stats, isLoading, error } = useQuery<DetailedProjectStats>({
    queryKey: ['project-statistics', projectId],
    queryFn: async () => {
      try {
        const response = await api.get(`/projects/${projectId}/statistics`)
        return response.data
      } catch (error) {
        console.error('Error fetching project statistics:', error)
        throw error
      }
    },
    enabled: !!projectId,
    retry: 2,
    retryDelay: 1000
  })

  // Dynamic typology stats for this project
  const { data: typologyStats, isLoading: isTypologyLoading, refetch: refetchTypology } = useQuery({
    queryKey: ['project-typology-stats', projectId],
    queryFn: async () => {
      const res = await api.get(`/projects/${projectId}/statistics/typology`)
      return res.data as { data: any[]; total: any | null }
    },
    enabled: !!projectId,
    staleTime: 60 * 1000,
  })

  const tRows = (typologyStats?.data ?? []).filter((row: any) => row != null)
  const tTotal = typologyStats?.total ?? null

  const fInt = (n: number | null | undefined) => (n || 0).toLocaleString('en-GB')
  const fNum = (n: number | null | undefined, digits = 2) =>
    n == null ? '—' : new Intl.NumberFormat('en-GB', { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(Number(n))
  const fCurrency = (n: number | null | undefined) =>
    n == null ? '—' : new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Number(n))

  // Get the selected typology data
  const getSelectedTypologyStats = () => {
    if (!selectedTypology || !tRows) return null
    return tRows.find((row: any) => row.typology === selectedTypology)
  }

  // Override stats with selected typology stats if available
  const displayStats = getSelectedTypologyStats() 
    ? {
        ...stats,
        totalUnits: getSelectedTypologyStats()?.nr_units || stats.totalUnits,
        avgPrice: getSelectedTypologyStats()?.avg_price_unit || stats.avgPrice,
        minPrice: getSelectedTypologyStats()?.min_price_unit || stats.minPrice,
        maxPrice: getSelectedTypologyStats()?.max_price_unit || stats.maxPrice,
        avgSqmPrice: getSelectedTypologyStats()?.avg_price_per_sqm || stats.avgSqmPrice,
        minSqmPrice: getSelectedTypologyStats()?.avg_price_per_sqm || stats.minSqmPrice,
        maxSqmPrice: getSelectedTypologyStats()?.avg_price_per_sqm || stats.maxSqmPrice,
        minGpa: getSelectedTypologyStats()?.min_area || stats.minGpa,
        avgGpa: getSelectedTypologyStats()?.avg_area || stats.avgGpa,
        maxGpa: getSelectedTypologyStats()?.max_area || stats.maxGpa,
        minTotalGpa: getSelectedTypologyStats()?.min_area || stats.minTotalGpa,
        avgTotalGpa: getSelectedTypologyStats()?.avg_area || stats.avgTotalGpa,
        maxTotalGpa: getSelectedTypologyStats()?.max_area || stats.maxTotalGpa,
      }
    : stats

  const conversionRateValue = useMemo(() => {
    const totalUnits = Number(displayStats?.totalUnits ?? 0)
    if (totalUnits <= 0) return null

    const soldUnits = Number(displayStats?.soldUnits ?? 0)
    const reservedUnits = Number(displayStats?.reservedUnits ?? 0)
    const rate = ((soldUnits + reservedUnits) / totalUnits) * 100

    return Number.isFinite(rate) ? Math.round(rate) : null
  }, [displayStats?.totalUnits, displayStats?.soldUnits, displayStats?.reservedUnits])

  // Simulate staggered loading for better UX
  useEffect(() => {
    if (stats && !isLoading) {
      const kpiOrder = [
        'total-units',
        'conversion-rate', 
        'avg-price',
        'avg-sqm-price',
        'gpa-range',
        'total-gpa-range',
        'time-to-sell',
        'time-since-listing',
        'parking'
      ]
      
      kpiOrder.forEach((kpi, index) => {
        setTimeout(() => {
          setLoadedKPIs(prev => new Set([...prev, kpi]))
          setLoadingKPIs(prev => {
            const newSet = new Set(prev)
            newSet.delete(kpi)
            return newSet
          })
        }, index * 200) // 200ms delay between each KPI
      })
    }
  }, [stats, isLoading])

  // Helper function to check if a KPI is loading
  const isKPILoading = (kpiId: string) => {
    return isLoading || loadingKPIs.has(kpiId) || !loadedKPIs.has(kpiId)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(9)].map((_, i) => (
            <CompactStatCard
              key={i}
              icon={Home}
              title="Loading..."
              value=""
              subtitle=""
              color="gray"
              isLoading={true}
            />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <BarChart3 className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading statistics
            </h3>
            <div className="mt-2 text-sm text-red-700">
              Unable to load project statistics. This might be due to:
              <ul className="mt-2 list-disc list-inside space-y-1">
                <li>No units found for this project</li>
                <li>Database connection issues</li>
                <li>Missing project data</li>
              </ul>
              <div className="mt-3">
                <button 
                  onClick={() => window.location.reload()} 
                  className="text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!stats) return null

  return (
    <div className="space-y-6">
      {/* Typology breakdown for this project */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-secondary-900">Typology Breakdown</h3>
          <button
            onClick={() => refetchTypology()}
            className="btn-secondary flex items-center"
            disabled={isTypologyLoading}
          >
            <Loader2 className={`h-4 w-4 mr-2 ${isTypologyLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-600">Typology</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-secondary-600">Nr units</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-secondary-600">Ave Price p/sqm</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-secondary-600">Min area</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-secondary-600">Ave Area</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-secondary-600">Max Area</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-secondary-600">Min Price Unit</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-secondary-600">Ave Price Unit</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-secondary-600">Max Price Unit</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {(isTypologyLoading ? Array.from({ length: 6 }, () => ({})) : tRows).map((row: any, idx: number) => (
                <tr 
                  key={idx} 
                  className={`hover:bg-gray-50 ${row?.typology ? 'cursor-pointer' : ''} ${selectedTypology === row?.typology ? 'bg-gray-100' : ''}`} 
                  onClick={() => {
                    if (row?.typology) {
                      setSelectedTypology(row.typology)
                      console.log('Selected typology:', row.typology)
                    }
                  }}
                >
                  <td className="px-3 py-2 text-sm font-medium text-secondary-900">{isTypologyLoading ? '—' : row?.typology || '—'}</td>
                  <td className="px-3 py-2 text-sm text-right">{isTypologyLoading ? '—' : fInt(row?.nr_units)}</td>
                  <td className="px-3 py-2 text-sm text-right">{isTypologyLoading ? '—' : fCurrency(row?.avg_price_per_sqm)}</td>
                  <td className="px-3 py-2 text-sm text-right">{isTypologyLoading ? '—' : `${fNum(row?.min_area, 0)} m²`}</td>
                  <td className="px-3 py-2 text-sm text-right">{isTypologyLoading ? '—' : `${fNum(row?.avg_area, 0)} m²`}</td>
                  <td className="px-3 py-2 text-sm text-right">{isTypologyLoading ? '—' : `${fNum(row?.max_area, 0)} m²`}</td>
                  <td className="px-3 py-2 text-sm text-right">{isTypologyLoading ? '—' : fCurrency(row?.min_price_unit)}</td>
                  <td className="px-3 py-2 text-sm text-right">{isTypologyLoading ? '—' : fCurrency(row?.avg_price_unit)}</td>
                  <td className="px-3 py-2 text-sm text-right">{isTypologyLoading ? '—' : fCurrency(row?.max_price_unit)}</td>
                </tr>
              ))}
              {tTotal && !isTypologyLoading && (
                <tr className="bg-secondary-50 font-semibold">
                  <td className="px-3 py-2 text-sm text-secondary-900">TOTAL</td>
                  <td className="px-3 py-2 text-sm text-right">{fInt(tTotal.nr_units)}</td>
                  <td className="px-3 py-2 text-sm text-right">{fCurrency(tTotal.avg_price_per_sqm)}</td>
                  <td className="px-3 py-2 text-sm text-right">{`${fNum(tTotal.min_area, 0)} m²`}</td>
                  <td className="px-3 py-2 text-sm text-right">{`${fNum(tTotal.avg_area, 0)} m²`}</td>
                  <td className="px-3 py-2 text-sm text-right">{`${fNum(tTotal.max_area, 0)} m²`}</td>
                  <td className="px-3 py-2 text-sm text-right">{fCurrency(tTotal.min_price_unit)}</td>
                  <td className="px-3 py-2 text-sm text-right">{fCurrency(tTotal.avg_price_unit)}</td>
                  <td className="px-3 py-2 text-sm text-right">{fCurrency(tTotal.max_price_unit)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Compact Grid Layout - 3 columns for better space utilization */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Key Metrics Row */}
        <CompactStatCard
          icon={Home}
          title="Total Units"
          value={displayStats.totalUnits}
          subtitle={`${displayStats.availableUnits} available, ${displayStats.soldUnits} sold`}
          color="blue"
          isLoading={isKPILoading('total-units')}
        />
        
        <CompactStatCard
          icon={Target}
          title="Conversion Rate"
          value={formatPercentage(conversionRateValue, 0)}
          subtitle="(Sold + Reserved) vs total"
          color="green"
          isLoading={isKPILoading('conversion-rate')}
        />
        
        <CompactStatCard
          icon={DollarSign}
          title="Average Price"
          value={formatCurrency(displayStats.avgPrice)}
          subtitle={`${formatCurrency(displayStats.minPrice)} - ${formatCurrency(displayStats.maxPrice)}`}
          color="purple"
          isLoading={isKPILoading('avg-price')}
        />
        
        <CompactStatCard
          icon={TrendingUp}
          title="Avg Price/m²"
          value={displayStats.avgSqmPrice ? formatCurrency(displayStats.avgSqmPrice) : '—'}
          subtitle={displayStats.minSqmPrice || displayStats.maxSqmPrice
            ? `${displayStats.minSqmPrice ? formatCurrency(displayStats.minSqmPrice) : '—'} - ${displayStats.maxSqmPrice ? formatCurrency(displayStats.maxSqmPrice) : '—'}`
            : '—'}
          color="blue"
          isLoading={isKPILoading('avg-sqm-price')}
        />
        
        <CompactStatCard
          icon={Ruler}
          title="Private GPA Range"
          value={`${formatNumber(displayStats.minGpa)} - ${formatNumber(displayStats.maxGpa)} m²`}
          subtitle={`Average: ${formatNumber(displayStats.avgGpa)} m²`}
          color="blue"
          isLoading={isKPILoading('gpa-range')}
        />
        
        <CompactStatCard
          icon={Ruler}
          title="Total GPA Range"
          value={`${formatNumber(displayStats.minTotalGpa)} - ${formatNumber(displayStats.maxTotalGpa)} m²`}
          subtitle={`Average: ${formatNumber(displayStats.avgTotalGpa)} m²`}
          color="green"
          isLoading={isKPILoading('total-gpa-range')}
        />
        
        <CompactStatCard
          icon={Clock}
          title="Avg Time to Sell"
          value={displayStats.avgMonthsToSell > 0 ? `${formatNumber(displayStats.avgMonthsToSell, 1)} months` : 'N/A'}
          subtitle={displayStats.avgDaysToSell > 0 ? `${formatNumber(displayStats.avgDaysToSell, 0)} days` : ''}
          color="purple"
          isLoading={isKPILoading('time-to-sell')}
        />
        
        <CompactStatCard
          icon={Calendar}
          title="Time Since Listing"
          value={`${displayStats.timeSinceListingMonths} months`}
          subtitle={displayStats.listingDate ? `Since ${new Date(displayStats.listingDate).toLocaleDateString('pt-PT')}` : ''}
          color="blue"
          isLoading={isKPILoading('time-since-listing')}
        />
        
        <CompactStatCard
          icon={Car}
          title="Parking"
          value={`${displayStats.parkingPercentage}%`}
          subtitle={`${displayStats.unitsWithParking}/${displayStats.totalUnits} units`}
          color="green"
          isLoading={isKPILoading('parking')}
        />
      </div>

      {/* Project Yield Information - Only show if data exists */}
      {(displayStats.projectYield > 0 || displayStats.yieldDuration > 0) && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Project Yield</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CompactStatCard
              icon={Percent}
              title="Project Yield"
              value={formatPercentage(displayStats.projectYield)}
              subtitle="Expected return"
              color="green"
            />
            <CompactStatCard
              icon={Clock}
              title="Yield Duration"
              value={`${displayStats.yieldDuration} years`}
              subtitle="Expected duration"
              color="blue"
            />
          </div>
        </div>
      )}
    </div>
  )
}
